import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { ScrollText } from 'lucide-react'
import { attemptsQuery, type Attempt } from '@/api/access'
import { clientName, clientNamesQuery } from '@/api/passes'
import { EntryShell, type EntryContext } from '@/app/access/entry-shell'
import { StateFilter } from '@/ui/state-filter'
import { clockTime, localDate, refusalText } from '@/domain/access'
import { errorText } from '@/domain/errors'
import { formatDate, t } from '@/i18n'
import { DataList, type DataColumn } from '@/ui/data-list'
import { InlineBanner } from '@/ui/inline-banner'
import { EmptyState } from '@/ui/page'
import { LoadingBlock } from '@/ui/spinner'

type Outcome = 'all' | 'refused'

export const Route = createFileRoute('/_app/c/$orgId/entry/log')({
  validateSearch: (search: Record<string, unknown>): { only?: 'refused' } =>
    search['only'] === 'refused' ? { only: 'refused' } : {},
  component: Log,
})

function Log() {
  const { orgId } = Route.useParams()
  return (
    <EntryShell orgId={orgId} tab="log" allowed={(can) => can.visits}>
      {(ctx) => <LogScreen key={ctx.branch.id} {...ctx} />}
    </EntryShell>
  )
}

/// «Журнал»: every try at the door, newest first — «чому мене не пустили о
/// 18:02» (specs/access.md §8). The last 200; «Відмови» narrows to refusals.
function LogScreen({ orgId, branch, timeZone }: EntryContext) {
  const { only } = Route.useSearch()
  const navigate = Route.useNavigate()
  const outcome: Outcome = only ?? 'all'
  const attempts = useQuery(attemptsQuery(orgId, branch.id, outcome))
  const rows = attempts.data ?? []
  const ids = [...new Set(rows.flatMap((a) => (a.clientId ? [a.clientId] : [])))].sort()
  const names = useQuery(clientNamesQuery(orgId, ids))
  const known = new Map((names.data ?? []).map((client) => [client.id, clientName(client)]))
  const s = t.access.log
  const today = localDate(new Date(), timeZone)

  const when = (iso: string) => {
    const day = localDate(new Date(iso), timeZone)
    return day === today ? clockTime(iso, timeZone) : `${formatDate(day)}, ${clockTime(iso, timeZone)}`
  }

  const columns: readonly DataColumn<Attempt>[] = [
    { key: 'at', header: s.columns.at, width: 'minmax(112px,0.7fr)', mobile: 'trailing', cell: (a) => <span className="tabular">{when(a.at)}</span> },
    {
      key: 'client',
      header: s.columns.client,
      width: 'minmax(0,1.1fr)',
      mobile: 'title',
      cell: (a) => (a.clientId ? (known.get(a.clientId) ?? t.passes.sold.unknownClient) : <span className="text-muted">{s.unknownClient}</span>),
    },
    { key: 'method', header: s.columns.method, width: 'minmax(0,0.6fr)', hideBelow: 'md', cell: (a) => t.access.methods[a.method] ?? a.method },
    {
      key: 'outcome',
      header: s.columns.outcome,
      width: '112px',
      mobile: 'subtitle',
      cell: (a) => (
        <span className="inline-flex items-center gap-1.5">
          <span aria-hidden="true" className={`size-1.5 rounded-full ${a.outcome === 'allowed' ? 'bg-success' : 'bg-danger'}`} />
          {a.direction === 'out' ? s.exit : a.outcome === 'allowed' ? s.allowed : s.refusedOne}
        </span>
      ),
    },
    {
      key: 'reason',
      header: s.columns.reason,
      width: 'minmax(0,2fr)',
      mobile: 'subtitle',
      cell: (a) => (a.outcome === 'refused' ? refusalText(a.reason) : null),
    },
  ]

  return (
    <div className="flex flex-col gap-4">
      <StateFilter
        label={s.filter}
        value={outcome}
        options={[
          { value: 'all', label: s.all, count: undefined },
          { value: 'refused', label: s.refused, count: undefined },
        ]}
        onChange={(next) => void navigate({ search: next === 'refused' ? { only: 'refused' } : {}, replace: true })}
      />
      {attempts.isPending ? (
        <LoadingBlock label={t.app.loading} />
      ) : attempts.isError && !attempts.data ? (
        <InlineBanner kind="error" message={errorText(attempts.error)} action={{ label: t.app.retry, onClick: () => void attempts.refetch() }} />
      ) : rows.length === 0 && outcome === 'all' ? (
        <EmptyState icon={<ScrollText />} title={s.empty} />
      ) : (
        <DataList
          label={t.access.tabs.log}
          columns={columns}
          rows={rows}
          rowKey={(a) => a.id}
          rowTone={(a) => (a.direction === 'out' ? 'muted' : 'normal')}
          empty={s.emptyRefused}
          footer={rows.length >= 200 ? <span className="tabular">{s.shown(rows.length)}</span> : undefined}
        />
      )}
    </div>
  )
}
